require('./bootstrap');

import Vue from 'vue';
import { sync } from 'vuex-router-sync';
import store from './store';
import router from './router';
import jwt from './utils/jwt';
import VueAnalytics from 'vue-analytics'
import * as Sentry from "@sentry/vue";
import { initPostHog } from '@/modules/analytics/posthog';
import Intersect from 'vue-intersect'
import { register as registerSwiperElements } from 'swiper/element/bundle'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/autoplay'

registerSwiperElements()
Vue.config.ignoredElements = [
    ...(Vue.config.ignoredElements || []),
    /^swiper-/
]

Sentry.init({
    Vue,
    dsn: LaraVue.sentry.vue,
    environment: LaraVue.environment,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.vueIntegration({
            tracingOptions: {
                trackComponents: true,
            },
        }),
    ],
    tracesSampleRate: LaraVue.environment === 'production' ? 0.1 : 1.0,
    ignoreErrors: LaraVue.sentry.ignoreErrors,
    initialScope: {
        tags: { project: '[Website] Mental Health Academy - US' },
    },
});

initPostHog();

Vue.use(VueAnalytics, {
    id: 'UA-26500785-1',
    router,
    autoTracking: {
        transformQueryString: false
    },
    debug: {
        sendHitTask: process.env.NODE_ENV === 'production'
    }
})

import GhApp from './views/app.vue';
import GhBreadcrumb from './components/breadcrumb/breadcrumb.vue';
import GhBreadcrumbRedesign from './components/breadcrumb/breadcrumbRedesign.vue';
import GhBreadcrumbItem from './components/breadcrumb/breadcrumbItem.vue';
import GhBreadcrumbItemRedesign from './components/breadcrumb/breadcrumbItemRedesign.vue';
import GhNewsletterView from '@/modules/newsletter/views/Index.vue';
import GhRevealSlide from './components/transitions/revealSlide.vue';
import GhNotice from '@/components/ui/notice';

Vue.component('ghApp', GhApp.default || GhApp);
Vue.component('ghBreadcrumb', GhBreadcrumb.default || GhBreadcrumb);
Vue.component('ghBreadcrumbRedesign', GhBreadcrumbRedesign.default || GhBreadcrumbRedesign);
Vue.component('ghBreadcrumbItem', GhBreadcrumbItem.default || GhBreadcrumbItem);
Vue.component('ghBreadcrumbItemRedesign', GhBreadcrumbItemRedesign.default || GhBreadcrumbItemRedesign);
Vue.component('ghNewsletterView', GhNewsletterView.default || GhNewsletterView);
Vue.component('ghRevealSlide', GhRevealSlide.default || GhRevealSlide);
Vue.component('ghNotice', GhNotice.default || GhNotice);
Vue.component('intersect', Intersect);

/*
 * Directives
 */
import fetching from './directives/fetching';
import spy from './directives/spy';
import print from './directives/print';

const directives = [
    fetching,
    spy,
    print
];

directives.forEach(directive => {
    Vue.directive(directive.name, directive);
});

sync(store, router);

/*
 * Set HTTP Request Header
 */
axios.interceptors.request.use(config => {
    config.headers['X-CSRF-TOKEN'] = window.LaraVue.csrfToken;
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    if(jwt.getToken()) config.headers['Authorization'] = 'Bearer '+ jwt.getToken();

    return config;
}, error => {
    return Promise.reject(error);
});

axios.interceptors.response.use(
    response => {
        /**
         * If the response contains authorization header,
         * Then we assume that it is from the renew token request.
         * Set the new JWT token.
         */
        const authHeader = response.headers.authorization;
        if (authHeader) jwt.setToken(authHeader.substr(7));

        return response;
    },
    error => {
        const errorResponseData = error.response.data;
        /**
         * Expired token but still refreshable will hit this condition.
         * We then trying to renew the expired token.
         * Also passing the failed request as a callback to retry failed request.
         */
        if (errorResponseData.message === 'Token has expired' && errorResponseData.statusCode === 401) {
            return store.dispatch('auth/renewToken', error.response.config)
                .then(response => {
                    if (response.status === 201 || response.status === 200) {
                        return Promise.resolve(response);
                    }
                });
        }

        return Promise.reject(error);
    }
);

/**
 * Because our application depends on authenticated user to render appropriate components
 * So we will bootstrap Vue after attempting to authenticate user.
 * Clear any cached token on auth failure.
 */
const initVue = () => {
    new Vue({
        el: '#gh-app', store, router,

        data () {
            return {
                greenhat: LaraVue.app.greenhat || 'https://www.greenhat.net'
            }
        },

        methods: {
            checkIsTouchDevice() {
                const isTouch = window.innerWidth < 1024;
                store.commit('ui/SET_TOUCH_STATE', isTouch);
                return isTouch;
            },

            /**
             * Get device based on screen innerWidth.
             * Following are just common device widths and there are no perfect breakpoints.
             * We are totally able to add more breakpoints if really need.
             */
            getDevice() {
                const width = window.innerWidth;
                let device = '';

                if (width < 768) {
                    device = 'mobile';
                } else if (width >= 768 && width <= 1023) {
                    device = 'tablet';
                } else if (width > 1023 && width <= 1215) {
                    device = 'desktop';
                } else {
                    device = 'widescreen';
                }

                store.commit('ui/SET_DEVICE', device);

                return device;
            }
        },

        created() {
            window.addEventListener('resize', this.checkIsTouchDevice);
            window.addEventListener('resize', this.getDevice);
            this.checkIsTouchDevice();
            this.getDevice();
            this.scrollMagicController = new ScrollMagic.Controller();
        },
    });
}

store.dispatch('auth/getAuthUser')
    .then(_ => initVue())
    .catch(_ => {
        jwt.clearToken()
        initVue()
    })

